import {
  ListOverview,
  WorkflowPerformance,
  WorkflowStatsResponse,
} from "./types";
import {
  MessageSquare,
  Mail,
  MessageCircle,
  BellRing,
  GitBranch,
  TrendingUp,
  AlertTriangle,
  Clock,
  List,
  Users,
  Building2,
  HardHat,
  Grid2X2,
  Zap,
  LucideIcon,
} from "lucide-react";

export const periodLabels: Record<string, string> = {
  today: "Today",
  this_week: "This Week",
  this_month: "This Month",
};
export const transformChannelData = (channelType: string, stats: any) => {
  const template = colorMap[channelType];
  if (!template) return [];

  return template
    .map(({ name, key, color }) => ({
      name,
      value: stats[key] ?? 0,
      color,
    }))
    .filter((entry) => entry.value > 0); // exclude zero values
};
export function isListOverview(obj: any): obj is ListOverview {
  return obj && typeof obj === "object" && "totalLists" in obj;
}
export function isWorkPerformance(obj: any): obj is WorkflowPerformance {
  return obj && typeof obj === "object" && "activeWorkflowsCount" in obj;
}
export const titleMap: Record<string, string> = {
  totalMessages: "Total Messages",
  totalEmails: "Emails Sent",
  totalSms: "SMS Sent",
  totalWhatsapp: "WhatsApp Sent",
  totalInApp: "In-App Notifications",
};
export const isWorkStats = (data: any): data is WorkflowStatsResponse[] => {
  return (
    Array.isArray(data) &&
    data.every(
      (item) =>
        typeof item.workflowId === "number" &&
        typeof item.workflowName === "string" &&
        typeof item.successCount === "number" &&
        typeof item.failureCount === "number" &&
        typeof item.successRate === "number"
    )
  );
};
export const isWorkflowStatsResponse = (
  data: any[] | WorkflowStatsResponse | null
): data is WorkflowStatsResponse => {
  return (
    data !== null &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    Array.isArray((data as WorkflowStatsResponse).results)
  );
};
export const colorMap: Record<
  string,
  { name: string; key: string; color: string }[]
> = {
  EMAIL: [
    { name: "Delivered", key: "deliveredCount", color: "#9b87f5" },
    { name: "Failed", key: "failedCount", color: "#ea384c" },
    { name: "Pending", key: "pendingCount", color: "#FEC6A1" },
  ],
  TEXT: [
    { name: "Delivered", key: "deliveredCount", color: "#9b87f5" },
    { name: "Failed", key: "failedCount", color: "#ea384c" },
    { name: "Pending", key: "pendingCount", color: "#FEC6A1" },
  ],
  WHATSAPP: [
    { name: "Delivered", key: "deliveredCount", color: "#9b87f5" },
    { name: "Failed", key: "failedCount", color: "#ea384c" },
    { name: "Pending", key: "pendingCount", color: "#FEC6A1" },
  ],
  INAPP: [
    { name: "Delivered", key: "deliveredCount", color: "#9b87f5" },
    { name: "Seen", key: "seenCount", color: "#7E69AB" },
    { name: "Failed", key: "failedCount", color: "#ea384c" },
    { name: "Pending", key: "pendingCount", color: "#FEC6A1" },
  ],
};
export const variantStyles = {
  default: "bg-muted/50",
  primary: "bg-primary/5",
  success: "bg-green-50 dark:bg-green-900/10",
  warning: "bg-amber-50 dark:bg-amber-900/10",
  info: "bg-sky-50 dark:bg-sky-900/10",
  white: "bg-white",
};

export const iconColors = {
  default: "text-muted-foreground",
  primary: "text-primary",
  success: "text-green-600 dark:text-green-400",
  warning: "text-amber-600 dark:text-amber-400",
  info: "text-sky-600 dark:text-sky-400",
};

export const valueColors = {
  default: "text-foreground",
  primary: "text-primary",
  success: "text-green-600 dark:text-green-400",
  warning: "text-amber-600 dark:text-amber-400",
  info: "text-sky-600 dark:text-sky-400",
};

export const progressColors = {
  default: "",
  success: "text-green-600",
  warning: "text-amber-600",
  info: "text-blue-600",
};

// Channel type configurations
export const CHANNEL_ICONS: Record<string, LucideIcon> = {
  totalMessages: MessageSquare,
  totalEmails: Mail,
  totalSms: MessageSquare,
  totalWhatsapp: MessageCircle,
  totalInApp: BellRing,
  EMAIL: Mail,
  TEXT: MessageSquare,
  WHATSAPP: MessageCircle,
  NOTIFICATION: BellRing,
  INAPP: BellRing,
};

export const CHANNEL_COLORS: Record<string, string> = {
  totalMessages: "text-primary",
  totalEmails: "text-blue-500",
  totalSms: "text-emerald-500",
  totalWhatsapp: "text-emerald-500",
  totalInApp: "text-purple-500",
  EMAIL: "text-blue-500",
  TEXT: "text-emerald-500",
  WHATSAPP: "text-emerald-500",
  NOTIFICATION: "text-purple-500",
  INAPP: "text-purple-500",
};

// Workflow and performance icons
export const WORKFLOW_ICONS = {
  workflow: GitBranch,
  success: TrendingUp,
  failure: AlertTriangle,
  time: Clock,
  messages: MessageSquare,
} as const;

// List and user category icons
export const CATEGORY_ICONS = {
  consumer: Users,
  technicians: HardHat,
  businessUser: Building2,
  static: Grid2X2,
  dynamic: Zap,
  lists: List,
} as const;

export const CATEGORY_COLORS = {
  consumer: "text-blue-500",
  technicians: "text-amber-500",
  businessUser: "text-green-500",
  static: "text-primary",
  dynamic: "text-primary",
  lists: "text-primary",
} as const;

// Utility function to get icon component
export const getIconComponent = (type: string): LucideIcon => {
  return CHANNEL_ICONS[type] || MessageSquare;
};

// Utility function to get icon color
export const getIconColor = (type: string): string => {
  return CHANNEL_COLORS[type] || "text-muted-foreground";
};

// Channel configuration for better maintainability
export const CHANNEL_CONFIG = {
  EMAIL: {
    title: "Email Performance",
    type: "EMAIL",
    dataType: "EMAIL",
    icon: Mail,
    color: "text-blue-500",
  },
  TEXT: {
    title: "SMS Performance",
    type: "TEXT",
    dataType: "TEXT",
    icon: MessageSquare,
    color: "text-emerald-500",
  },
  WHATSAPP: {
    title: "WhatsApp Performance",
    type: "WHATSAPP",
    dataType: "WHATSAPP",
    icon: MessageCircle,
    color: "text-emerald-500",
  },
  NOTIFICATION: {
    title: "In-App Performance",
    type: "INAPP",
    dataType: "NOTIFICATION",
    icon: BellRing,
    color: "text-purple-500",
  },
} as const;
